import { Link } from 'react-router';
import { Card, ClickableIcon, Icon } from '@zenid/web';
import { useCallback, useContext } from 'react';
import type { AccountSchema, AuthenticationMethodSchema } from '@zenid/mypage-api';
import { css, cx } from '../../../../../styled-system/css';
import { flex } from '../../../../../styled-system/patterns';
import { getCurrentTimestampMs } from '../../../../utils/time';
import type { HighschoolAccountInfo } from '../../../highschool';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useEmailResendDialog } from '../../hooks/useEmailResendDialog';
import { useVerificationEmail } from '../../hooks/useVerificationEmail';
import { useMfaResetAlertDialog } from '../../hooks/useMfaResetAlertDialog';
import { ResendEmailAlert } from './resendEmail/ResendEmailAlert/ResendEmailAlert';
import { MfaResetAlertDialog } from './resetMfa/MfaResetAlertDialog/MfaResetAlertDialog';
import { SnackbarContext } from '#/utils/snackbar/snackbarProvider';

export type LoginAndSecurityProps = {
  account: AccountSchema;
  authenticationMethods: AuthenticationMethodSchema[] | null;
  highschoolAccountInfo?: HighschoolAccountInfo;
};

export function LoginAndSecurity({
  account: { id, email, emailVerified, changePasswordUrl },
  authenticationMethods,
  highschoolAccountInfo,
}: LoginAndSecurityProps) {
  const { copy } = useCopyToClipboard();

  // N高アカウントかつ未統合の場合は Enterprise Connection のみなので MFA 設定不可能
  const editDisabled = highschoolAccountInfo?.integrated === false;

  return (
    <div className={flex({ minWidth: '343px', flexDirection: 'column', sm: { maxWidth: '720px' } })}>
      <div
        className={flex({
          flexDirection: 'row',
          alignItems: 'center',
          gap: '{spacing.4}',
          marginBottom: '{spacing.20}',
        })}
      >
        <Icon name="lock" color="colors.text.main" size="m" />
        <h2 className={css({ textStyle: 'headding.s', sm: { textStyle: 'headding.l' } })}>ログインとセキュリティ</h2>
      </div>
      <Card className={flex({ backgroundColor: '{colors.background.white}' })}>
        <ul
          className={flex({
            width: '100%',
            flexDirection: 'column',
          })}
        >
          <li className={listItemStyle}>
            <div
              className={flex({
                width: '100%',
                flexDirection: 'column',
                borderBottom: '1px solid {colors.border.light}',
                padding: '{spacing.16} 0',
                gap: '{spacing.8}',
                sm: { flexDirection: 'row', padding: '{spacing.20} 0', gap: '{spacing.16}' },
              })}
            >
              <p className={itemNameStyle}>ユーザーID</p>
              <div className={flex({ width: '100%', alignItems: 'center', gap: '{spacing.8}', alignSelf: 'stretch' })}>
                <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })} data-dd-privacy="mask">
                  {id}
                </p>
                <ClickableIcon name="content_copy" color="colors.text.sub" size="s" onClick={() => copy(id)} />
              </div>
            </div>
          </li>
          <EmailGroup email={email} emailVerified={emailVerified} highschoolAccountInfo={highschoolAccountInfo} />
          {!editDisabled && changePasswordUrl && <ChangePasswordSection changePasswordUrl={changePasswordUrl} />}
          {!editDisabled && <MFAResetAlertSection authenticationMethods={authenticationMethods} />}
        </ul>
      </Card>
    </div>
  );
}

function EmailGroup({
  email,
  emailVerified,
  highschoolAccountInfo,
}: {
  email: string;
  emailVerified: boolean;
  highschoolAccountInfo?: HighschoolAccountInfo;
}) {
  // 高校の情報がない場合は一般ユーザ向けの表示を
  if (!highschoolAccountInfo) {
    return <EmailSection email={email} emailVerified={emailVerified} />;
  }

  if (highschoolAccountInfo.integrated) {
    // 統合済の場合は高校メールアドレスを「メールアドレス」欄に、統合先のアカウントのメールアドレスを「サブメールアドレス」に表示
    return (
      <>
        <EmailSection email={highschoolAccountInfo.email} emailVerified disableEdit />
        <EmailSection email={email} emailVerified={emailVerified} title="サブメールアドレス" />
      </>
    );
  } else {
    // 未統合の場合は統合ボタンを表示 (未統合の場合は email と highschoolAccountInfo.email は同値)
    return (
      <>
        <EmailSection email={email} emailVerified disableEdit />
        <AddSubEmailSection />
      </>
    );
  }
}

function AddSubEmailSection() {
  return (
    <li
      className={cx(
        listItemStyle,
        css({
          _hover: {
            background:
              'linear-gradient(90deg, rgba(66, 137, 232, 0.00) 0%, rgba(66, 137, 232, 0.10) 8%, rgba(66, 137, 232, 0.10) 92%, rgba(66, 137, 232, 0.00) 100%)',
          },
        })
      )}
    >
      <div
        className={flex({
          width: '100%',
          flexDirection: 'column',
          borderBottom: '1px solid {colors.border.light}',
          padding: '{spacing.16} 0',
          gap: '{spacing.12}',
          sm: { padding: '{spacing.20} 0' },
        })}
      >
        <Link to="/integration/form" data-testid="integration-link">
          <div
            className={flex({
              width: '100%',
              flexDirection: 'row',
            })}
          >
            <div
              className={flex({
                width: '100%',
                gap: '{spacing.8}',
                flexDirection: 'column',
                sm: { flexDirection: 'row', gap: '{spacing.16}' },
              })}
            >
              <p className={itemNameStyle}>サブメールアドレス</p>
              <div className={flex({ flexDirection: 'column' })}>
                <p
                  className={css({
                    textStyle: 'body.m.default',
                    color: '{colors.text.main}',
                    wordBreak: 'break-all',
                  })}
                  data-dd-privacy="mask"
                >
                  追加する
                </p>
              </div>
            </div>
            <div className={flex({ alignItems: 'center' })}>
              <Icon name="navigate_next" color="colors.text.sub" size="s" />
            </div>
          </div>
        </Link>
      </div>
    </li>
  );
}

function EmailSection({
  email,
  emailVerified,
  title = 'メールアドレス',
  disableEdit,
}: {
  email: string;
  emailVerified: boolean;
  title?: string;
  disableEdit?: boolean;
}) {
  return (
    <li
      className={cx(
        listItemStyle,
        css({
          _hover: {
            background:
              'linear-gradient(90deg, rgba(66, 137, 232, 0.00) 0%, rgba(66, 137, 232, 0.10) 8%, rgba(66, 137, 232, 0.10) 92%, rgba(66, 137, 232, 0.00) 100%)',
          },
        })
      )}
    >
      <div
        className={flex({
          width: '100%',
          flexDirection: 'column',
          borderBottom: '1px solid {colors.border.light}',
          padding: '{spacing.16} 0',
          gap: '{spacing.12}',
          sm: { padding: '{spacing.20} 0' },
        })}
      >
        {disableEdit ? (
          <EmailSectionInner email={email} emailVerified={emailVerified} title={title} disableEdit={disableEdit} />
        ) : (
          <>
            <Link to="/edit/email" data-testid="email-link">
              <EmailSectionInner email={email} emailVerified={emailVerified} title={title} />
            </Link>
            {emailVerified || <EmailNotification email={email} />}
          </>
        )}
      </div>
    </li>
  );
}

function EmailSectionInner({
  email,
  emailVerified,
  title,
  disableEdit,
}: {
  email: string;
  emailVerified: boolean;
  title: string;
  disableEdit?: boolean;
}) {
  return (
    <div
      className={flex({
        width: '100%',
        flexDirection: 'row',
      })}
    >
      <div
        className={flex({
          width: '100%',
          gap: '{spacing.8}',
          flexDirection: 'column',
          sm: { flexDirection: 'row', gap: '{spacing.16}' },
        })}
      >
        <p className={itemNameStyle}>{title}</p>
        <div className={flex({ flexDirection: 'column' })}>
          <p
            className={css({
              textStyle: 'body.m.default',
              color: '{colors.text.main}',
              wordBreak: 'break-all',
            })}
            data-dd-privacy="mask"
          >
            {email}
          </p>
          <div className={flex({ alignItems: 'center' })}>
            {emailVerified ? (
              <>
                <Icon name="check_fill_off" color="colors.action.success.main" size="s" />
                <p className={css({ textStyle: 'caption.m', color: '{colors.text.sub}' })}>認証済</p>
              </>
            ) : (
              <>
                <Icon name="error_fill_off" color="colors.text.sub" size="s" />
                <p className={css({ textStyle: 'caption.m', color: '{colors.text.sub}' })}>未認証</p>
              </>
            )}
          </div>
        </div>
      </div>
      {!disableEdit && (
        <div className={flex({ alignItems: 'center' })}>
          <Icon name="navigate_next" color="colors.text.sub" size="s" />
        </div>
      )}
    </div>
  );
}

function ChangePasswordSection({ changePasswordUrl }: { changePasswordUrl: string }) {
  return (
    <li
      className={cx(
        listItemStyle,
        css({
          _hover: {
            background:
              'linear-gradient(90deg, rgba(66, 137, 232, 0.00) 0%, rgba(66, 137, 232, 0.10) 8%, rgba(66, 137, 232, 0.10) 92%, rgba(66, 137, 232, 0.00) 100%)',
          },
        })
      )}
    >
      <a href={changePasswordUrl} className={css({ width: '100%' })} data-testid="change-password-url">
        <div
          className={flex({
            width: '100%',
            flexDirection: 'row',
            borderBottom: '1px solid {colors.border.light}',
            padding: '{spacing.16} 0',
            sm: { padding: '{spacing.20} 0' },
          })}
        >
          <div
            className={flex({
              width: '100%',
              flexDirection: 'column',
              gap: '{spacing.8}',
              sm: { gap: '{spacing.16}', flexDirection: 'row' },
            })}
          >
            <p className={itemNameStyle}>パスワード</p>
            <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })}>変更する</p>
          </div>
          <div className={flex({ alignItems: 'center' })}>
            <Icon name="navigate_next" color="colors.text.sub" size="s" />
          </div>
        </div>
      </a>
    </li>
  );
}

function EmailNotification({ email }: { email: string }) {
  const verificationEmail = useVerificationEmail();
  const emailResendDialog = useEmailResendDialog({ sendVerificationEmail: verificationEmail.sendVerificationEmail });
  if (verificationEmail.error) {
    return null;
  }
  return (
    <div className={css({ maxWidth: '468px', sm: { marginLeft: '156px' } })}>
      <ResendEmailAlert email={email} verificationEmail={verificationEmail} emailResendDialog={emailResendDialog} />
    </div>
  );
}

function MFAResetAlertSection({
  authenticationMethods,
}: {
  authenticationMethods: AuthenticationMethodSchema[] | null;
}) {
  const { createMfaUrl, deleteMfaMethod, isOpen, setIsOpen, closeDialog } =
    useMfaResetAlertDialog(authenticationMethods);
  const snackbar = useContext(SnackbarContext);
  const hasAuthenticationMethod = authenticationMethods === null ? false : authenticationMethods.length !== 0;
  const resetMfa = useCallback(async () => {
    const mfaEnrollmentUrl = await createMfaUrl();
    console.log(`mfaEnrollmentUrl=${mfaEnrollmentUrl}`);
    if (!mfaEnrollmentUrl) {
      snackbar.add({
        id: getCurrentTimestampMs(),
        message: '二段階認証のリセットに失敗しました',
        notificationLevel: 'error',
        hideCloseButon: false,
      });
      return;
    }
    try {
      await deleteMfaMethod();
    } catch (e) {
      console.error(e);
      snackbar.add({
        id: getCurrentTimestampMs(),
        message: '二段階認証のリセットに失敗しました',
        notificationLevel: 'error',
        hideCloseButon: false,
      });
      return;
    }
    const windowOpenSuccess = window.open(mfaEnrollmentUrl);
    if (windowOpenSuccess) {
      closeDialog();
    } else {
      // ポップアップブロックされた場合はリダイレクト
      location.href = mfaEnrollmentUrl;
    }
  }, [createMfaUrl, deleteMfaMethod, snackbar, closeDialog]);
  const handleOpenChange = useCallback(
    async (isOpen: boolean) => {
      if (!isOpen) {
        setIsOpen(false);
        return;
      }
      if (hasAuthenticationMethod) {
        setIsOpen(true);
        return;
      }
      setIsOpen(false);
      const mfaEnrollmentUrl = await createMfaUrl();
      if (!mfaEnrollmentUrl) {
        snackbar.add({
          id: getCurrentTimestampMs(),
          message: '二段階認証の変更処理に失敗しました',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      window.open(mfaEnrollmentUrl);
    },
    [createMfaUrl, setIsOpen, snackbar, hasAuthenticationMethod]
  );
  return (
    <li
      className={cx(
        listItemStyle,
        css({
          _hover: {
            background:
              'linear-gradient(90deg, rgba(66, 137, 232, 0.00) 0%, rgba(66, 137, 232, 0.10) 8%, rgba(66, 137, 232, 0.10) 92%, rgba(66, 137, 232, 0.00) 100%)',
          },
        })
      )}
    >
      <MfaResetAlertDialog onOpenChange={handleOpenChange} open={isOpen} onClickOk={resetMfa} onClickNG={closeDialog}>
        <button
          className={css({
            width: '100%',
            _hover: {
              cursor: 'pointer',
            },
          })}
        >
          <div
            className={flex({
              width: '100%',
              flexDirection: 'row',
              borderBottom: '1px solid {colors.border.light}',
              padding: '{spacing.16} 0',
              sm: { padding: '{spacing.20} 0' },
            })}
          >
            <div
              className={flex({
                width: '100%',
                flexDirection: 'column',
                gap: '{spacing.8}',
                sm: { gap: '{spacing.16}', flexDirection: 'row' },
              })}
            >
              <p className={itemNameStyle}>2段階認証</p>
              <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })}>
                {hasAuthenticationMethod ? 'リセット' : '設定する'}
              </p>
            </div>
            <div className={flex({ alignItems: 'center' })}>
              <Icon name="navigate_next" color="colors.text.sub" size="s" />
            </div>
          </div>
        </button>
      </MfaResetAlertDialog>
    </li>
  );
}

const listItemStyle = flex({
  position: 'relative',
  alignItems: 'center',
  alignSelf: 'stretch',
  margin: '0 {spacing.16}',
  sm: { margin: '0 {spacing.24}' },
});

const itemNameStyle = flex({
  width: '100%',
  maxWidth: '311px',
  alignItems: 'center',
  color: '{colors.text.main}',
  textStyle: 'body.s.defalut',
  sm: { maxWidth: '140px' },
});
